import axios, { AxiosError } from "axios"
import type { AxiosAdapter, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { z } from "zod"
import { isTenantClosedRefusal, markTenantClosed, tenantClosureOf } from "@/lib/tenant-closed"
import type { AcceptInviteRequest, ApiError, AvailableTenantApi, InvitationPreview, LoginRequest, LoginResponse, MeResponse, ForgotPasswordRequest, ResetPasswordRequest, User, UserRole } from "@/types/api"

const API_BASE_URL = "/api/v1"

function normalizeUser(user: User): User {
  return { ...user, role: user.role.toLowerCase() as UserRole }
}

/** Méthodes qui modifient l'état du serveur. */
const WRITE_METHODS = new Set(["post", "put", "patch", "delete"])

/**
 * Serveur indisponible ou réponse coupée en route.
 * 520 et 524 sont les coupures du frontal Cloudflare.
 */
const UNAVAILABLE_STATUSES: ReadonlySet<number> = new Set([502, 503, 504, 520, 524])

/**
 * Rejeu sans risque d'une requête en échec ?
 * Une lecture toujours. Une écriture seulement sur 503 : rien d'écrit.
 * Coupure (502, 504, 520, 524, réseau) : l'écriture a pu passer.
 * Un 503 `*_RENDER_BUSY` n'est jamais rejoué, ni `TURNSTILE_UNAVAILABLE`.
 * Son rejeu userait une des trois tentatives d'inscription.
 * @example isReplayable(O.some("post"), O.some(504), O.none) // false
 */
export function isReplayable(method: O.Option<string>, status: O.Option<number>, code: O.Option<string>): boolean {
  const refusedForGood = pipe(
    code,
    O.exists((c) => c.endsWith("_RENDER_BUSY") || c === "TURNSTILE_UNAVAILABLE"),
  )
  if (refusedForGood) return false
  const writes = pipe(
    method,
    O.exists((m) => WRITE_METHODS.has(m.toLowerCase())),
  )
  return writes ? pipe(status, O.exists((s) => s === 503)) : true
}

/** Requête rejouée une fois après le rafraîchissement de la session. */
type ReplayedRequest = InternalAxiosRequestConfig & { sessionReplayed: true }

function markReplayed(config: InternalAxiosRequestConfig): ReplayedRequest {
  return { ...config, sessionReplayed: true }
}

const PAYLOAD_TOO_LARGE_STATUS = 413

/** Problème rendu quand un routeur refuse un corps trop gros. */
const ROUTER_TOO_LARGE_PROBLEM: ApiError = {
  type: "about:blank",
  title: "Payload Too Large",
  status: PAYLOAD_TOO_LARGE_STATUS,
  code: "REQUEST_BODY_TOO_LARGE",
  detail: "Le fichier ou la requête dépasse la taille autorisée : réduisez-la puis réessayez.",
}

/** Corps d'un refus de l'API : un problème à `code`. */
const PROBLEM_WITH_CODE = z.object({ code: z.string().trim().min(1) })

/**
 * Un routeur (ingress, nginx) répond 413 en HTML ou sans corps.
 * Le client en tire le même problème que l'API.
 * @example describeRouterTooLarge(error).response?.data.code // "REQUEST_BODY_TOO_LARGE"
 */
function describeRouterTooLarge(error: AxiosError<ApiError>): AxiosError<ApiError> {
  return pipe(
    O.fromNullable(error.response),
    O.filter((response) => response.status === PAYLOAD_TOO_LARGE_STATUS && !PROBLEM_WITH_CODE.safeParse(response.data).success),
    O.match(
      () => error,
      (response) =>
        new AxiosError<ApiError>(error.message, error.code, error.config, error.request, {
          ...response,
          data: ROUTER_TOO_LARGE_PROBLEM,
        }),
    ),
  )
}

export class ApiService {
  private client: AxiosInstance
  private isRefreshing = false
  private failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason: unknown) => void }> = []

  private processQueue(error: unknown): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(undefined)
      }
    })
    this.failedQueue = []
  }

  private clearAuthAndRedirect(): void {
    localStorage.removeItem("user")
    const currentPath = window.location.pathname + window.location.search
    const authPaths = ["/login", "/register", "/forgot-password", "/reset-password", "/accept-invite"]
    const isPublicPath = authPaths.some((p) => currentPath.startsWith(p))
    // Portal routes have their own auth flow (portal_token + /portal/login).
    // A 401 from the admin api client on a portal page means a stale admin
    // token is still in localStorage; do not yank the user back to admin login.
    const isPortalPath = currentPath.startsWith("/portal/")
    if (!isPublicPath && !isPortalPath) {
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
    }
  }

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      withCredentials: true, // Send HttpOnly cookies automatically
    })

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (rawError: AxiosError<ApiError>) => {
        const error = describeRouterTooLarge(rawError)
        const status = error.response?.status

        // Network error or API unavailable — don't clear auth, retry once after 2s
        if (!status || UNAVAILABLE_STATUSES.has(status)) {
          const config = error.config
          const method = pipe(
            O.fromNullable(config),
            O.chain((c) => O.fromNullable(c.method)),
          )
          const code = pipe(
            O.fromNullable(error.response?.data),
            O.chain((problem) => O.fromNullable(problem.code)),
            O.filter((c): c is string => typeof c === "string"),
          )
          if (!isReplayable(method, O.fromNullable(status), code)) {
            throw error
          }
          // Only retry once: skip if this is already a retry
          if (config && !(config as unknown as Record<string, unknown>).__isRetry) {
            (config as unknown as Record<string, unknown>).__isRetry = true
            return new Promise<AxiosResponse>((resolve, reject) => {
              setTimeout(() => {
                this.client.request(config).then(resolve).catch(reject)
              }, 2000)
            })
          }
          return Promise.reject(error)
        }

        // 401 Unauthorized — try to refresh the access token before giving up
        const requestUrl = error.config?.url || ""
        const isLoginRequest = requestUrl.includes("/auth/login")
        const isRefreshRequest = requestUrl.includes("/auth/refresh")
        const isAuthRequest = isLoginRequest || isRefreshRequest

        if (status === 401 && !isAuthRequest) {
          const originalRequest = error.config!

          // Rejeu déjà tenté : on s'arrête.
          // Un 401 n'a rien écrit : le rejeu est sûr.
          if ("sessionReplayed" in originalRequest) {
            throw error
          }

          // If already refreshing, queue this request to retry after refresh completes
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject })
            }).then(() => this.client.request(markReplayed(originalRequest)))
          }

          this.isRefreshing = true

          return this.client
            .post("/auth/refresh", {})
            .then(
              () => {
                this.isRefreshing = false
                this.processQueue(null)
                // Un refus du rejeu vise la requête, pas la session.
                return this.client.request(markReplayed(originalRequest))
              },
              (refreshError) => {
                this.isRefreshing = false
                this.processQueue(refreshError)
                this.clearAuthAndRedirect()
                throw refreshError
              },
            )
        }
        // Organisme fermé : un avis unique remplace l'écran.
        if (isTenantClosedRefusal(error)) {
          markTenantClosed(tenantClosureOf(error))
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Remplace le transport HTTP : un faux serveur en test.
   * @example new ApiService().useTransport(fakeServer([200])).get("/documents")
   */
  useTransport(transport: AxiosAdapter): this {
    this.client.defaults.adapter = transport
    return this
  }

  // Legacy methods kept for backward compatibility during migration
  setToken(_token: string): void {
    // No-op: tokens are now in HttpOnly cookies
  }

  clearToken(): void {
    localStorage.removeItem("user")
  }

  getToken(): string {
    return "" // Tokens are in HttpOnly cookies, not accessible via JS
  }

  // Generic request methods
  get<T, P = Record<string, unknown>>(url: string, params?: P, headers?: Record<string, string>): Promise<T> {
    return this.client.get<T>(url, { params, headers }).then((res: AxiosResponse<T>) => res.data)
  }

  post<T>(url: string, data?: unknown): Promise<T> {
    return this.client.post<T>(url, data).then((res: AxiosResponse<T>) => res.data)
  }

  // Multipart upload — axios computes the boundary itself when we hand it a
  // FormData body and let it pick the Content-Type (we override the instance
  // default JSON header per-request). The CSRF header (X-Requested-With) and the
  // HttpOnly auth cookie come from the shared client instance.
  postForm<T>(url: string, form: FormData, onProgress?: (percent: number) => void): Promise<T> {
    // `__isRetry` : jamais de rejeu après une coupure.
    // Un rejeu créerait un doublon.
    // Un 401 reste rejoué : le serveur a refusé avant d'écrire.
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event: { loaded: number; total?: number }) => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100))
      },
      __isRetry: true,
    }
    return this.client.post<T>(url, form, config).then((res: AxiosResponse<T>) => res.data)
  }

  put<T>(url: string, data?: unknown): Promise<T> {
    return this.client.put<T>(url, data).then((res: AxiosResponse<T>) => res.data)
  }

  patch<T>(url: string, data?: unknown): Promise<T> {
    return this.client.patch<T>(url, data).then((res: AxiosResponse<T>) => res.data)
  }

  delete<T>(url: string): Promise<T> {
    return this.client.delete<T>(url).then((res: AxiosResponse<T>) => res.data)
  }

  // Auth methods
  login(data: LoginRequest): Promise<LoginResponse> {
    return this.post<LoginResponse>("/auth/login", data).then((response) => {
      response.user = normalizeUser(response.user)
      // Tokens are set as HttpOnly cookies by the backend (Set-Cookie header)
      // Only store user info for display purposes
      localStorage.setItem("user", JSON.stringify(response.user))
      return response
    })
  }

  /**
   * Re-issues the session cookies for another organisation of the same user.
   * The response has the login shape: its `user` carries the role, plan and
   * permissions of the TARGET organisation, which differ from the current ones.
   */
  switchTenant(tenantId: string): Promise<LoginResponse> {
    return this.post<LoginResponse>("/auth/switch-tenant", { tenant_id: tenantId }).then((response) => {
      response.user = normalizeUser(response.user)
      localStorage.setItem("user", JSON.stringify(response.user))
      return response
    })
  }

  forgotPassword(data: ForgotPasswordRequest): Promise<{ message: string }> {
    return this.post<{ message: string }>("/auth/forgot-password", data)
  }

  resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
    return this.post<{ message: string }>("/auth/reset-password", data)
  }

  logout(): Promise<void> {
    return this.post<void>("/auth/logout").then(() => {
      this.clearToken()
    }).catch(() => {
      this.clearToken()
    })
  }

  acceptInvite(invitation: AcceptInviteRequest): Promise<LoginResponse> {
    // comply-ignore: api-route-version-prefix — la baseURL du client porte déjà /api/v1.
    return this.post<LoginResponse>("/auth/accept-invite", invitation).then((response) => {
      response.user = normalizeUser(response.user)
      localStorage.setItem("user", JSON.stringify(response.user))
      return response
    })
  }

  previewInvitation(token: string): Promise<InvitationPreview> {
    return this.get<InvitationPreview>(`/auth/invite/${encodeURIComponent(token)}/preview`)
  }

  refreshToken(): Promise<{ access_token: string; token_type: string; expires_in: number }> {
    // With HttpOnly cookie auth, the refresh token is sent automatically via cookies
    return this.post<{ access_token: string; token_type: string; expires_in: number }>("/auth/refresh", {})
  }

  getCurrentUser(): Promise<MeResponse> {
    return this.get<MeResponse>("/auth/me").then((response) => {
      response.user = normalizeUser(response.user)
      return response
    })
  }

  /**
   * Fetch the list of tenants the authenticated user can switch into.
   * This endpoint mirrors the `available_tenants` field of /auth/me but is
   * useful when callers only need the membership list without re-fetching
   * the full user profile.
   */
  listAvailableTenants(): Promise<AvailableTenantApi[]> {
    return this.get<{ available_tenants: AvailableTenantApi[] }>("/auth/tenants")
      .then((response) => response.available_tenants)
  }

  /**
   * Marque le flag tenant `has_seen_first_contract_send` (issue #37 §15.2) :
   * la modale didactique du premier envoi de contrat de sous-traitance pour
   * signature ne se réaffichera plus pour aucun utilisateur du tenant.
   */
  markFirstContractSendSeen(): Promise<{ has_seen_first_contract_send: boolean }> {
    return this.post<{ has_seen_first_contract_send: boolean }>("/tenant/first-contract-send-seen", {})
  }
}

export const api = new ApiService()
